import React, { useEffect, useState } from "react";
import dayjs from "dayjs";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  Grid2,
} from "@mui/material";
import MUIDatePicker from "../../../components/MUIDatePicker/MUIDatePicker";
import AppButton from "../../../components/CommonReusableComponent/AppButton/AppButton";
import { addCurrentTimeInTimeStamp } from "../../../common/CommonFunctions";
import { Icons } from "../../../config/MuiIcons.config";
import AppAutoCompleteField from "../../../components/CommonReusableComponent/AppAutoCompleteField/AppAutoCompleteField";

const InternalAuditFilterModal = ({
  open,
  onClose,
  defaultFilters,
  auditTypeList,
  auditTypeCategoryList,
  statusList,
  frequencyList,
  getFilteredDataByField,
  getDateRangeFromFrequency,
  onApplyFilters,
  initialFilters
}) => {

  const [localFilters, setLocalFilters] = useState({
    status: defaultFilters.status,
    auditTypeCategoryId: defaultFilters.auditTypeCategoryId,
    auditTypeId: defaultFilters.auditTypeId,
    auditFrequency: defaultFilters.auditFrequency,
    fromDate: defaultFilters.fromDate,
    toDate: defaultFilters.toDate,
  });

  const disablePreviousDateForToDate = (date) => {
    const selectedDate = dayjs(date).startOf("day");
    const minDate = dayjs(localFilters?.fromDate).startOf("day");
    return selectedDate.isBefore(minDate);
  };

  const handleFrequencyChange = (Data) => {
    if (Data) {
      const { value } = Data;
      const { fromDate_, toDate_ } = getDateRangeFromFrequency(value);
      setLocalFilters((prev) => ({
        ...prev,
        auditFrequency: value,
        fromDate: fromDate_,
        toDate: toDate_,
      }));
    }
  };

  const handleFilters = (Data, FieldName) => {
    setLocalFilters((prev) => ({
      ...prev,
      [FieldName]: Data?.value || null,
    }));

    // Dynamically filter data based on the changed field
    if (FieldName === "auditTypeCategoryId") {
      getFilteredDataByField(FieldName, Data?.value || null);
      setLocalFilters((prev) => ({
        ...prev,
        "auditTypeId": "All",
      }));
    }
  };

  // Is audit frequency set to custom
  const isAuditFrequencySetToCustom =
    localFilters?.auditFrequency === "custom" ? true : false;

  // Function get count of active filters applied on data table 
  const getActiveFilterCount = (isReset = false) => {
    const data = isReset ? initialFilters : localFilters;

    return Object.keys(data).filter((key) => {
      // Skip fields you don’t want to count
      if (["fromDate", "toDate", "auditFrequency"].includes(key)) return false;

      // Compare with initial
      return data[key] !== initialFilters[key];
    }).length;
  };

  // Function to apply fiters
  const applyFilters = () => {
    onApplyFilters(
      { ...localFilters }, 
      getActiveFilterCount()
    )
  }

  // Function to reset filters
  const resetFilters = () => {
    const {
      auditTypeId,
      auditTypeCategoryId,
      status,
      fromDate,
      toDate,
      auditFrequency,
    } = initialFilters;

    setLocalFilters({
      auditTypeId,
      auditTypeCategoryId,
      status,
      fromDate,
      toDate,
      auditFrequency,
    });
    onApplyFilters(
      {
        auditTypeId,
        auditTypeCategoryId,
        status,
        fromDate,
        toDate,
        auditFrequency,
      },
      getActiveFilterCount(true)
    );
  }; 

  return (
    <React.Fragment>
      <Dialog
        open={open}
        aria-labelledby="form-dialog-title"
        maxWidth="sm"
        fullWidth
        slotProps={{
          style: {
            opacity: 0.7,
          },
        }}
      >
        <DialogTitle id="responsive-dialog-title">
          <Typography
            className="inter-medium-500"
            style={{
              fontSize: "18px",
            }}
          >
            Filters
          </Typography>
          <IconButton
            aria-label="close"
            onClick={() => onClose()}
            style={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Icons.Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <div>
            <Grid2 container spacing={2}>
              <Grid2 size={{ xs: 12, sm: 6 }}>
                <AppAutoCompleteField
                  IsMultiple={false}
                  Options={auditTypeCategoryList || []}
                  TextInputName={"auditTypeCategoryId"}
                  TextInputId={"auditTypeCategoryId"}
                  Id={"auditTypeCategoryId"}
                  TextInputLabel={"Select audit type category"}
                  TextInputPlaceholder={"Select audit type category"}
                  HandleOnChange={(event, value) => {
                    handleFilters(value, "auditTypeCategoryId");
                  }}
                  Value={localFilters?.auditTypeCategoryId}
                />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6 }}>
                <AppAutoCompleteField
                  IsMultiple={false}
                  Options={auditTypeList || []}
                  TextInputName={"auditTypeId"}
                  TextInputId={"auditTypeId"}
                  Id={"auditTypeId"}
                  Disabled={localFilters?.auditTypeCategoryId === "All"}
                  TextInputLabel={"Select audit type"}
                  TextInputPlaceholder={"Select audit type"}
                  HandleOnChange={(event, value) => {
                    handleFilters(value, "auditTypeId");
                  }}
                  Value={localFilters?.auditTypeId}
                />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6 }}>
                <AppAutoCompleteField
                  IsMultiple={false}
                  Options={statusList || []}
                  SortOptions={false}
                  TextInputName={"status"}
                  TextInputId={"status"}
                  Id={"status"}
                  TextInputLabel={"Select audit status"}
                  TextInputPlaceholder={"Select audit status"}
                  HandleOnChange={(event, value) => {
                    handleFilters(value, "status");
                  }}
                  Value={localFilters?.status}
                />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6 }}>
                <AppAutoCompleteField
                  IsMultiple={false}
                  Options={frequencyList || []}
                  SortOptions={false}
                  Value={localFilters?.auditFrequency}
                  TextInputLabel={"Select audit frequency"}
                  TextInputPlaceholder={"Select audit frequency"}
                  HandleOnChange={(event, value) => {
                    handleFrequencyChange(value);
                  }}
                />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6 }}>
                <MUIDatePicker
                  EnablePast={true}
                  Error={false}
                  Disabled={!isAuditFrequencySetToCustom}
                  Label={"From date"}
                  Name={"fromDate"}
                  Id={"fromDate"}
                  OnChange={(event) => {
                    const { value } = event?.target;
                    handleFilters(
                      { value: addCurrentTimeInTimeStamp(value) },
                      "fromDate"
                    );
                  }}
                  Value={localFilters?.fromDate}
                />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6 }}>
                <MUIDatePicker
                  EnablePast={true}
                  Error={false}
                  Label={"To date"}
                  Name={"toDate"}
                  Id={"toDate"}
                  Disabled={!isAuditFrequencySetToCustom}
                  ShouldDisableCurrentDate={disablePreviousDateForToDate}
                  OnChange={(event) => {
                    const { value } = event?.target;
                    handleFilters(
                      { value: addCurrentTimeInTimeStamp(value) },
                      "toDate"
                    );
                  }}
                  Value={localFilters?.toDate}
                />
              </Grid2>
            </Grid2>
            <Grid2 container spacing={1} justifyContent={"right"}>
              <Grid2 size={{ xs: 12, sm: 6 }} className="my-2">
                <AppButton Color={"error"} OnPress={resetFilters}>
                  Reset
                </AppButton>
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6 }} className="my-2">
                <AppButton OnPress={applyFilters}>Apply Filters</AppButton>
              </Grid2>
            </Grid2>
          </div>
        </DialogContent>
      </Dialog>
    </React.Fragment>
  );
};

export default InternalAuditFilterModal;